import React from 'react';

import styled from 'styled-components';
import { Box, Button, Stack, Typography } from '@mui/material';
import { colorsNeutrals, light, neutrals } from 'utils/styles/color';
import { StaticDatePicker } from '@mui/lab';
import ListItemButton from '@mui/material/ListItemButton';

// *******************************
// DATE RANGE PICKER FILTER STYLES
// *******************************
export const StyledDateRangeFilterHeader = styled(Stack)`
  && {
    flex-direction: row;
    justify-content: space-between;
    padding: 0 16px;
    align-items: center;
    height: 39px; // <-- since border bottom has 1px size
    border-bottom: 1px solid ${colorsNeutrals[100]};

    span {
      display: block;

      color: ${neutrals[700]};
      font-family: Poppins, sans-serif;
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 400;
      line-height: 16px;
    }

    & > div {
      flex: 1;
      flex-direction: row;
      align-items: center;
    }

    & > .MuiButton-root {
      color: ${({ theme }) => theme.palette.primary.main};
      font-family: Poppins, sans-serif;
      font-size: 11px;
      font-style: normal;
      font-weight: 500;
      line-height: 15.5px;
      text-decoration-line: underline;

      padding: 0;
      min-width: unset;
    }
  }
`;

export const StyledDateLabel = styled(Box)<{
  $active?: boolean;
}>`
  && {
    flex: 1;
    text-align: center;
    background-color: ${({ $active }) =>
      $active ? light[100] : 'transparent'};
    border-radius: 4px;
    cursor: pointer;
  }
`;

export const StyledPresetHeader = styled(Box)`
  && {
    height: 32px;
    padding: 0 8px;

    display: flex;
    align-items: end;

    & > span {
      font-family: Poppins, sans-serif;
      font-weight: 300;
      font-size: 11px;
      line-height: 16px;
      letter-spacing: 10%;
      text-transform: uppercase;

      color: ${colorsNeutrals[400]};
    }
  }
`;

export const StyledDateRangeFilterAction = styled((props) => (
  <Typography component="span" {...props} />
))<{
  $active?: boolean;
}>`
  && {
    display: flex;
    height: 32px;
    padding: 0 16px;
    align-items: center;
    align-self: stretch;

    color: ${colorsNeutrals[700]};

    font-family: Poppins, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    cursor: pointer;

    ${({ $active }) =>
      $active &&
      `
        color: ${neutrals[500]};
        text-decoration-line: underline;
        &:before {
          content: '';
          position: absolute;
          left: 0;
          width: 3px;
          height: 10px;
          border-radius: var(--none, 0px) 1.5px 1.5px var(--none, 0px);
          background: ${neutrals[500]};
        }
      `}
  }
`;

export const StyledDatePickerWrapper = styled(Box)`
  && {
    & > div {
      width: 272px;
      min-width: 272px;
    }

    & .MuiCalendarPicker-root {
      & > div:first-of-type {
        padding: 0;
        margin: 0;
        margin-top: 4px;
        height: 32px;
        max-height: 32px;

        div {
          font-weight: 500 !important;
          font-size: 13px !important;
          color: ${neutrals[700]} !important;
        }
      }
    }

    .MuiTypography-caption {
      font-family: Poppins, sans-serif;
      font-weight: 300;
      font-size: 11px;
      line-height: 18.26px;
      letter-spacing: 0.4px;
      text-align: center;
      color: ${neutrals[400]};
    }

    .MuiButtonBase-root.MuiPickersDay-root {
      font-family: Poppins, sans-serif;
      font-weight: 400;
      font-size: 14px;
      line-height: 20.02px;
      letter-spacing: 0.17px;
      text-align: center;
      color: ${neutrals[700]};
    }

    .MuiButtonBase-root.MuiPickersDay-root.MuiPickersDay-dayOutsideMonth {
      color: ${neutrals[400]};
    }

    .MuiPickersDay-today {
      border: none !important;
      background: ${neutrals[100]};
    }

    .Mui-selected,
    .Mui-selected:hover,
    .Mui-selected:focus {
      background: ${neutrals[200]};
    }

    .MuiButtonBase-root.MuiPickersDay-root.Mui-disabled {
      color: ${neutrals[200]};
    }
  }
`;

export const StyledDatePicker = styled((props) => (
  <StaticDatePicker {...props} />
))(() => ({
  width: '272px',
  minWidth: '272px',
  '& .MuiCalendarPicker-root': {
    '& > div': {
      margin: 0,
    },
  },
}));

// *******************************
// LIST FILTER STYLES
// *******************************
export const StyledListFilterHeader = styled(Stack)<{
  $showResetButton: boolean;
}>`
  && {
    flex-direction: row;
    padding: 0 16px;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid ${neutrals[100]};
    height: 39px;

    .MuiInputBase-root {
      color: ${neutrals[700]};
      font-family: Poppins, sans-serif;
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 400;
      line-height: 16px;
    }

    .MuiButtonBase-root {
      opacity: ${({ $showResetButton }) => ($showResetButton ? 1 : 0)};
    }
  }
`;

export const StyledListFilterButton = styled(ListItemButton)`
  && {
    height: 32px;
    padding: 0 16px;
    gap: 16px;
    &:hover {
      background-color: transparent;
    }
    .MuiListItemIcon-root {
      min-width: unset;
    }
    .MuiCheckbox-root {
      padding: 0;
      margin-left: 0;
    }
    .MuiCheckbox-root.Mui-disabled {
      color: red;
      .MuiSvgIcon-root {
        color: ${neutrals[500]};
      }
    }
    .MuiSvgIcon-root {
      color: ${neutrals[500]};
    }
    .MuiTypography-root {
      color: ${colorsNeutrals[700]};
      font-family: Poppins, sans-serif;
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 400;
      line-height: 16px; /* 108.333% */
    }

    &.Mui-disabled {
      opacity: 1 !important;
    }
  }
`;

export const StyledListActionRow = styled(Stack)`
  && {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    height: 32px;

    & > div:first-of-type {
      display: flex;
      flex-direction: row;
      gap: 32px;
    }

    & > div:nth-of-type(2) {
      display: flex;
      flex-direction: row;
      gap: 8px;
    }
  }
`;

export const StyledListActionButton = styled(Button)`
  && {
    color: ${({ theme }) => theme.palette.primary.main};
    font-family: Poppins, sans-serif;
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 15.5px;
    text-decoration-line: underline;

    padding: 0;
    min-width: unset;

    &:hover {
      text-decoration-line: underline;
      background: transparent;
    }
  }
`;

export const StyledListSwitchText = styled(Typography)`
  && {
    font-family: Poppins, sans-serif;
    font-weight: 600;
    font-size: 10px;
    line-height: 15.5px;
    letter-spacing: 0%;
    color: ${neutrals[700]};

    margin: 0;
  }
`;
